/**
 * Type definitions for the shared <AgentBuilder scope> component.
 *
 * Phase 2 of PR-A AgentBuilder shared-component refactor (2026-05-11).
 * Per plan: BuilderConfig uses the SYSADMIN shape as the union (it
 * adds `slug: string` over the tenant shape). The tenant adapter sets
 * `slug: ''` on save and its serializer omits the slug field. Do NOT
 * narrow types in consumers.
 */
import type {
  AgentCategory,
  AgentAutonomy,
  AgentTrigger,
  AgentVisibility,
  AgentModelTask,
  GuardrailProfile,
} from '@/lib/mock-agents-data';

// ─── Builder scope ──────────────────────────────────────────────────────────

export type BuilderScope = 'tenant' | 'sysadmin';

// ─── BuilderConfig (sysadmin shape; tenant treats `slug` as ignored) ────────

export interface BuilderConfig {
  /**
   * Catalog-identity key (sysadmin only). Tenant adapter never reads
   * this — its `serializeSavePayload()` omits the field on save and
   * tenant `setIdentity` patches leave it at empty string.
   */
  slug: string;
  name: string;
  description: string;
  icon: string;
  iconColor: string;
  category: AgentCategory;
  visibility: AgentVisibility;
  systemPrompt: string;
  /**
   * Legacy single-model field. Tenant scope: brand_id string (server
   * resolves to argus_models.id). Sysadmin scope: argus_models UUID
   * (resolved on the client via applySetModelMutation()).
   */
  modelId: string;
  /**
   * Multi-model selection. Same scope discriminator as `modelId`:
   * tenant = brand_id strings; sysadmin = UUIDs.
   */
  modelIds: string[];
  modelTask: AgentModelTask;
  maxSteps: number;
  autonomyLevel: AgentAutonomy;
  temperature: number;
  maxOutputTokens: number;
  responseFormat: 'markdown' | 'json' | 'plain';
  selectedTools: string[];
  /** Sysadmin uses wrapper function keys; tenant scope leaves empty. */
  selectedFunctions: string[];
  selectedSkills: string[];
  /** Sysadmin uses workflow slugs; tenant scope leaves empty. */
  selectedWorkflows: string[];
  knowledgeSources: string[];
  guardrailProfile: GuardrailProfile;
  forbiddenTopics: string[];
  forbiddenActions: string[];
  triggerModes: AgentTrigger[];
  cronExpression: string;
  eventTypes: string[];
  // Advanced
  historyLength: number;
  contextStrategy: 'sliding_window' | 'summarization';
  retrievalCount: number;
  similarityThreshold: number;
  responseLengthProfile: 'brief' | 'standard' | 'detailed';
  followUpSuggestions: boolean;
  timeoutSeconds: number;
  maxRetries: number;
  maxTokenBudget: number;
  escalationTrigger: 'none' | 'on_error' | 'low_confidence' | 'always';
}

export interface BuilderMessage {
  id: string;
  role: 'argus' | 'user' | 'system';
  content: string;
  chips?: { id: string; label: string; emoji?: string; action: string }[];
}

export type BuilderPhase = 'discover' | 'design' | 'refine' | 'ready';

export const INITIAL_CONFIG: BuilderConfig = {
  slug: '',
  name: '',
  description: '',
  icon: 'bot',
  iconColor: 'blue',
  category: 'custom',
  visibility: 'team',
  systemPrompt: '',
  modelId: '',
  modelIds: [],
  modelTask: 'chat',
  maxSteps: 10,
  autonomyLevel: 'assisted',
  temperature: 0.5,
  maxOutputTokens: 4096,
  responseFormat: 'markdown',
  selectedTools: [],
  selectedFunctions: [],
  selectedSkills: [],
  selectedWorkflows: [],
  knowledgeSources: [],
  guardrailProfile: 'standard',
  forbiddenTopics: [],
  forbiddenActions: [],
  triggerModes: ['conversational'],
  cronExpression: '',
  eventTypes: [],
  historyLength: 20,
  contextStrategy: 'sliding_window',
  retrievalCount: 5,
  similarityThreshold: 0.7,
  responseLengthProfile: 'standard',
  followUpSuggestions: true,
  timeoutSeconds: 120,
  maxRetries: 3,
  maxTokenBudget: 50000,
  escalationTrigger: 'on_error',
};

// ─── Normalized catalog rows (adapters smooth the t.name/t.slug split) ──────

export interface NormalizedTool {
  /** Stable display id. Tenant: workspace_tools.id (uuid). Sysadmin: catalog id. */
  id: string;
  /** Compose key — tenant uses `name`; sysadmin uses `slug`. Either way the
   * field stored in BuilderConfig.selectedTools[] is THIS value. */
  slug: string;
  displayName: string;
  /** auto_approve | require_approval | forbidden (matches both sources). */
  policy: string;
}

export interface NormalizedSkill {
  /** Stable display id. Tenant uses skill.id; sysadmin uses catalog id. */
  id: string;
  /** Compose key stored in BuilderConfig.selectedSkills[]. Tenant: skill.id;
   * sysadmin: skill.slug. */
  slug: string;
  displayName: string;
  description: string;
  toolDependencies: string[];
}

export interface NormalizedFunction {
  id: string;
  /** Compose key (functionKey) stored in BuilderConfig.selectedFunctions[]. */
  slug: string;
  displayName: string;
  category: string;
  runtime: string;
  appliesTo: string;
  status: string;
}

export interface NormalizedWorkflow {
  id: string;
  slug: string;
  displayName: string;
  category: string;
  status: string;
}

export interface NormalizedLibraryRef {
  id: string;
  kind: 'folder' | 'article' | 'tag' | 'document' | 'document_folder' | 'source';
  displayName: string;
  description: string;
  status: string;
}

export interface NormalizedModel {
  /** UUID. Sysadmin uses this directly; tenant scope picker uses brandId
   * instead, but the type stays here for shared rendering. */
  id: string;
  brandId: string;
  displayName: string;
  isActive: boolean;
}

/**
 * Existing-agent shape after the adapter normalizes the response. Both
 * tenant (no edit mode) and sysadmin variants flow through here. Tenant
 * variant always returns null.
 */
export interface NormalizedExistingAgent {
  slug: string;
  displayName: string;
  description: string;
  systemPrompt: string;
  category: string;
  modelId: string | null;
  modelIds: string[] | undefined;
  toolSlugs: string[];
  functionKeys: string[];
  skillSlugs: string[];
  libraryRefs: Array<{
    kind: NormalizedLibraryRef['kind'];
    id: string;
  }>;
  workflowSlugs: string[];
  triggers: {
    conversational?: boolean;
    scheduled?: { cron: string; tz?: string } | null;
    event?: { types: string[] } | null;
  };
  behaviorConfig: {
    autonomyLevel?: 'read_only' | 'suggest' | 'auto_approve';
    maxSteps?: number;
    guardrailProfile?: string;
    responseFormat?: string;
    temperature?: number;
    maxOutputTokens?: number;
  };
  /** Original catalog row — passed back to the lifecycle action bar. */
  raw: unknown;
}

// ─── Adapter interface ──────────────────────────────────────────────────────
//
// The adapter centralizes scope-conditional hooks + payload shaping +
// affordance gating. Two implementations (tenantAdapter, sysadminAdapter)
// live in `AgentBuilder.adapters.tsx`.

export type SaveAgentArgs = {
  config: BuilderConfig;
  isEditing: boolean;
  editId: string | undefined;
  onSuccess: (createdId: string | null) => void;
  onError: (e: Error) => void;
};

export interface BuilderAdapter {
  scope: BuilderScope;

  // ── Catalog hooks ──
  useCatalog(): {
    tools: NormalizedTool[];
    skills: NormalizedSkill[];
    /** Null on tenant scope (no functions catalog wiring yet). */
    functions: NormalizedFunction[] | null;
    /** Null on tenant scope (no tenant clone catalog yet). */
    workflows: NormalizedWorkflow[] | null;
    libraryRefs: NormalizedLibraryRef[];
    libraryRefsLoading: boolean;
    libraryRefsError?: Error;
    models: NormalizedModel[];
    modelsLoading: boolean;
    skillsEmpty: boolean;
    toolsEmpty: boolean;
  };

  /** Sysadmin: fetch + normalize the existing agent for edit mode. Tenant:
   * always returns { existing: null, loading: false }. */
  useExistingAgent(editId: string | undefined): {
    existing: NormalizedExistingAgent | null;
    loading: boolean;
  };

  /** Returns the save-agent mutation. saveInFlight aggregates create+update. */
  useSaveAgent(): {
    saveInFlight: boolean;
    save(args: SaveAgentArgs): void;
  };

  /** Per-scope hallucinated-slug validators. Tenant returns undefined for
   * `validateAddFunction` so the apply-mutation switch skips it. */
  validateAddTool(slug: string, tools: NormalizedTool[]): boolean;
  validateAddSkill(slug: string, skills: NormalizedSkill[]): boolean;
  validateAddFunction?(slug: string, functions: NormalizedFunction[] | null): boolean;

  // ── Apply-mutation seams ──
  /** `set_identity` patch shaping. Sysadmin auto-derives `slug`. */
  transformSetIdentity(args: Record<string, unknown>, currentConfig: BuilderConfig): Partial<BuilderConfig>;
  /** `set_model` resolution. Sysadmin resolves brand → UUID; tenant writes
   * brand_id string directly. Returns `{ patch, error, loading }`. */
  resolveSetModel(
    args: Record<string, unknown>,
    models: NormalizedModel[],
    modelsLoading: boolean,
  ): {
    patch: { modelId: string; modelIds: string[] } | null;
    error: string | null;
    loading: boolean;
  };

  // ── Affordances ──
  /** Sysadmin renders <AgentLifecycleActionBar /> when editing. */
  showLifecycleBar: boolean;
  /** Sysadmin shows Functions picker as enabled; tenant scope renders a
   * disabled picker with a "preview" amber banner. */
  showFunctionsPicker: 'enabled' | 'disabled-preview' | 'hidden';
  /** Same gating for Workflows picker. */
  showWorkflowsPicker: 'enabled' | 'disabled-preview' | 'hidden';
  /** Section count for the progress bar. */
  totalSections: number;
  /** Sections used for completion-stats calculation. */
  sectionIndices: number[];
  /** Back-navigation target. */
  backRoute: string;
  /** Subtitle shown under the page header. */
  subtitle(isEditing: boolean): string;
  /** Returns the `getSectionStatus()` resolver this scope uses (sysadmin
   * has 10 sections with different ordering vs tenant's 8). */
  getSectionStatus(config: BuilderConfig, section: number): 'empty' | 'partial' | 'complete';

  // ── Save gate ──
  canSave(config: BuilderConfig, isEditing: boolean): boolean;

  // ── Helper-chat scope ──
  helperScope: 'tenant' | 'sysadmin';

  // ── Tools picker shape (tenant uses CatalogReferencePicker + legacy
  // checkbox grid; sysadmin uses pure checkbox grid). The shared body
  // renders both behind these flags. ──
  showCatalogPickerForTools: boolean; // tenant: true; sysadmin: false
  /** Tenant-only — the dual model+task+steps row uses a CatalogReferencePicker
   * for models; sysadmin uses a Select. */
  showCatalogPickerForModels: boolean;
  /** Sysadmin only — slug field in identity section. */
  showSlugField: boolean;
}

/**
 * Scope-deviation table for the shared <AgentBuilder> component.
 *
 * Bucket A — Hook swaps (catalog data sources):
 *   tenant.tools          = useWorkspaceTools()                 (t.name as compose key)
 *   sysadmin.tools        = useSysadminTools()                  (t.slug as compose key)
 *   tenant.skills         = useStudioSkills({activeOnly:true})  (.items)
 *   sysadmin.skills       = useCatalogSkills()                  (.pages.flatMap)
 *   tenant.workflows      = useCatalogWorkflows()  (read-only preview)
 *   sysadmin.workflows    = useCatalogWorkflows()  (selectable)
 *   tenant.functions      = useWrapperFunctions()  (read-only preview)
 *   sysadmin.functions    = useWrapperFunctions()  (selectable)
 *   tenant.models         = useTenantArgusModels() (brandIds)
 *   sysadmin.models       = useArgusModels()       (UUIDs)
 *   tenant.save           = useCreateAgent()       (no edit mode)
 *   sysadmin.save         = useCreateCatalogAgent() + useUpdateCatalogAgent() + useCatalogAgent()
 *
 * Bucket B — Affordances:
 *   sysadmin only          : AgentLifecycleActionBar (when editing), slug field,
 *                            "Editing system agent" subtitle, edit-mode spinner,
 *                            edit-mode seeding useEffect, Functions section enabled,
 *                            Workflows section enabled, 10 sections (vs 8)
 *   tenant only            : preselectModel via ?model= query param,
 *                            back→/agents/new, Functions/Workflows sections gated
 *                            as disabled "preview" with amber banner,
 *                            CatalogReferencePicker for tools + models
 *
 * Bucket C — Save payload (12 fields):
 *   field           tenant body                    sysadmin body
 *   slug            omitted                        slugify(name) or explicit
 *   name field      `name`                         `displayName`
 *   description     optional                       required (defaults to name)
 *   tools           tools: t.name[]                toolSlugs: slug[]
 *   skills          skills: id[]                   skillSlugs: slug[]
 *   library refs    knowledgeScope.libraryRefs     libraryRefs
 *   functions       omitted                        functionKeys
 *   workflows       omitted                        workflowSlugs
 *   model           modelBrandIds (brand strings)  modelIds[]+modelId (UUIDs)
 *   autonomy        inline ternary                 mapAutonomyToApi()
 *   triggers        flat triggerModes[]            nested modesToTriggers()
 *   behavior        flat                           nested behaviorConfig sub-obj
 *
 * Bucket D — applyMutation switch (16 cases; only 2 deviate):
 *   set_identity   sysadmin auto-derives slug when blank        (transformSetIdentity)
 *   set_model      sysadmin resolves brand→UUID via             (resolveSetModel)
 *                  applySetModelMutation; tenant writes brand string directly
 *   the other 14 cases are byte-identical between scopes.
 */
